// @vendors
const moment = require('moment');
const get = require('lodash/object/get');

// @helpers
const apiURLBuilder = require('../core/apiURLBuilder');

// @utilities
const { APIPost } = require('utilities/APIRequestHelper');

// @actions
const signaturePatternActions = require('actions/signaturePattern');
const errorActions = require('actions/brokerError');
const { outdateGlobalPosition } = require('actions/myGlobalPosition');
const { outdateMyInvestments } = require('actions/brokerMyInvestments');

// @constants
const actionTypes = require('constants/actionTypes');

const {
    WIZARD_SECOND_STEP,
    WIZARD_THIRD_STEP,
    FIRST_STEP,
    SECOND_STEP,
    THIRD_STEP,
    EURO_TEXT,
    LIMITED_ORDER_CLASS,
    BROKER_SYSTEM_NAME,
    BROKER_CONDITION_OVER_WARRANT_PRICE,
    BROKER_BUY_FOR_APPROX_IMPORT,
    CHARGE_ACCOUNT_PREFIX,
    DATE_FORMAT_API
} = require('constants/index');

const { BROKER_WARRANTS_ADVANCED_BUY_ID } = require('constants/operationTypes');

function reset() {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_RESET
    };
}

function setVisibleStep(step) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_SET_VISIBLE_STEP,
        payload: {
            step
        }
    };
}

function setWillCancel(value) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_WILL_CANCEL,
        payload: {
            value
        }
    };
}

function validateWithAPIRequest(step) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_VALIDATE_REQUEST,
        payload: {
            step
        }
    };
}

function validateWithAPISuccess(step, response) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_VALIDATE_SUCCESS,
        payload: {
            data: response,
            step
        }
    };
}

function validateWithAPIFailure(step, response) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_VALIDATE_FAILURE,
        payload: {
            data: response,
            step
        }
    };
}

function validateUIStep(step) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_VALIDATE_UI_STEP,
        payload: {
            step
        }
    };
}

const getValidationRequestObject = (getState) => {
    const immStocksContract = getState().brokerStocksContract;
    const immBrokerWarrantsAdvancedBuy = getState().brokerWarrantsAdvancedBuy;

    const step1 = immBrokerWarrantsAdvancedBuy.getIn(['steps', FIRST_STEP]);
    const step2 = immBrokerWarrantsAdvancedBuy.getIn(['steps', SECOND_STEP]);
    const selectedOptionValue = step2.get('selectedOptionValue');
    const validityDate = step1.get('validityDate');
    const selectedStockContract = immStocksContract.get(selectedOptionValue);
    const buyForApproxImport = step2.get('buyForApproxImport');
    const limitPrice = step2.get('limitPrice');
    const titleQuantity = step2.get('titleQuantity');
    const isin = immBrokerWarrantsAdvancedBuy.get('isin');
    const iso = immBrokerWarrantsAdvancedBuy.get('iso');

    const requestObject = {
        query: {
            codigoISIN: isin,
            codigoISO: iso,
            contratoValores: {
                numerodecontrato: selectedStockContract.get('walletNumber'),
                producto: selectedStockContract.get('productNumber')
            },
            digitoControlCCV: selectedStockContract.get('controlDigit'),
            fechaLimite: validityDate.format(DATE_FORMAT_API),
            importeAproximado: {
                divisa: EURO_TEXT,
                numeroimportevalores: buyForApproxImport
            },
            titulos: titleQuantity,
            clase: LIMITED_ORDER_CLASS,
            precioLimitada: limitPrice
        }
    };

    return requestObject;
};

const validateWithAPI = (step, history) => (dispatch, getState) => {
    const valid = getState().brokerWarrantsAdvancedBuy.getIn(['steps', SECOND_STEP, 'valid']);

    if (valid) {
        const data = getValidationRequestObject(getState);

        dispatch(validateWithAPIRequest(step));
        return APIPost(dispatch, apiURLBuilder.getURL('brokerWarrantsAdvancedBuyValidation'), data)
            .then(function(response) {
                dispatch(validateWithAPISuccess(step, response));
            }, function(response) {
                dispatch(validateWithAPIFailure(step, response));
                const errorKey = get(response, ["body", "error"], "");
                dispatch(errorActions.show("brokerAPIErrors-" + errorKey, true, history));
            }
        );
    }
};

function getConfirmationRequestObject(getState) {
    //Hardcoded service params
    //TODO: move to constants file if they are needed somewhere else
    const IND_ORDEN_BOLSA = 1;
    const NUM_EJECUCIONES = 1;
    const ID_ACCION = "COS";
    const TIPO_EJECUCION = 0;
    const TIPO_OPERACION = "C";
    const TIPO_PRECIO = "T";

    const immStocksContract = getState().brokerStocksContract;
    const immBrokerWarrantsAdvancedBuy = getState().brokerWarrantsAdvancedBuy;
    const immBrokerProductFile = getState().brokerProductFile;
    const immBrokerLegalDocumentation = getState().brokerLegalDocumentation;
    const uuid = immBrokerLegalDocumentation.get('uuid');

    const step1 = immBrokerWarrantsAdvancedBuy.getIn(['steps', FIRST_STEP]);
    const step2 = immBrokerWarrantsAdvancedBuy.getIn(['steps', SECOND_STEP]);
    const validationServiceResponse = immBrokerWarrantsAdvancedBuy.get('validationServiceResponse');
    const productCurrency = immBrokerWarrantsAdvancedBuy.get('productCurrency');
    const orderType = step1.get('orderType');
    const selectedOptionValue = step2.get('selectedOptionValue');
    const selectedStockContract = immStocksContract.get(selectedOptionValue);
    const titleQuantity = validationServiceResponse.get('ntitulos');
    const conditionType = step1.get('conditionType');
    const conditionTypeValue = step1.get('conditionTypeValue');
    const limitPrice = step2.get('limitPrice');
    const buyForApproxImport = parseFloat(step2.get('buyForApproxImport'));
    const amount = step2.get('buyFor') === BROKER_BUY_FOR_APPROX_IMPORT ? buyForApproxImport : 0;

    let spcSymbol = immBrokerProductFile.getIn(['warrant', 'underlying', 'spcSymbol']);
    let marketCode = immBrokerProductFile.getIn(['warrant', 'underlying', 'exchangeId']);
    let stockLast = immBrokerProductFile.getIn(['warrant','underlying','last']);

    if (orderType === BROKER_CONDITION_OVER_WARRANT_PRICE) {
        spcSymbol = immBrokerProductFile.getIn(['warrant', 'spcSymbol']);
        marketCode = immBrokerProductFile.getIn(['warrant', 'exchangeId']);
        stockLast = immBrokerProductFile.getIn(['warrant','offerPrice']);
    }

    const requestObject = {
        query: {
            codEmis: validationServiceResponse.get('codigoEmision').trim(),
            codGrupoSector: validationServiceResponse.get('codGrupoSector').trim(),
            codMercado: marketCode,
            codOrganismoEjecucion: validationServiceResponse.get('codOrganismoEjecucion').trim(),
            codOrganismoFusion: validationServiceResponse.get('codOrganismoFusion').trim(),
            codVaint: validationServiceResponse.get('codigoValor').trim(),
            codValorSibCodVLBOL: validationServiceResponse.get('codValorSibCodVLBOL').trim(),
            codigoMoneda: validationServiceResponse.get('codigoMoneda').trim(),
            contratoValores: {
                numerodecontrato: selectedStockContract.get('walletNumber'),
                producto: selectedStockContract.get('productNumber')
            },
            dataToken: immBrokerWarrantsAdvancedBuy.get('signaturePatternToken'),
            digControCCV: selectedStockContract.get('controlDigit'),
            fechaVencimiento: moment().format(DATE_FORMAT_API),
            idAccion: ID_ACCION,
            idCondicion: conditionType,
            identifiMercadoContratacion: validationServiceResponse.get('identifiMercadoContratacion').trim(),
            importeAproximado:{
              divisa: EURO_TEXT,
              importe: amount
            },
            indCompras: validationServiceResponse.get('indCompras').trim(),
            indEnvioRecepcion: validationServiceResponse.get('indEnvioRecepcion').trim(),
            indNegocio: validationServiceResponse.get('indNegocio').trim(),
            indOrdenBolsa: IND_ORDEN_BOLSA,
            indTratamientoEmisor: validationServiceResponse.get('indTratamientoEmisor').trim(),
            numEjecuciones: NUM_EJECUCIONES,
            numTitulos: titleQuantity,
            precio: stockLast,
            simboloEmision: spcSymbol.trim(),
            sistema: BROKER_SYSTEM_NAME,
            tipoCotizacion: validationServiceResponse.get('tipoCotizacion').trim(),
            tipoEjecucion: TIPO_EJECUCION,
            tipoMercado: validationServiceResponse.get('tipoMercado').trim(),
            tipoOperacion: TIPO_OPERACION,
            tipoPrecio: TIPO_PRECIO,
            valorCondicion: parseFloat(Math.round(conditionTypeValue * 100) / 100).toFixed(2),
            valorJuntaSindical: validationServiceResponse.get('valorJuntaSindical').trim(),
            precioLimitada: {
                divisa: productCurrency,
                numeroimportevalores: limitPrice
            },
            documentationId: uuid
        }
    };

    return requestObject;
}

function confirmWarrantsBuyRequest() {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_CONFIRM_REQUEST
    };
}

function confirmWarrantsBuySuccess() {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_CONFIRM_SUCCESS
    };
}

function confirmWarrantsBuyFailure(response) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_CONFIRM_FAILURE,
        payload: {
            data: response
        }
    };
}

function confirmWarrantsBuy(history) {
    return function(dispatch, getState) {
        const data = getConfirmationRequestObject(getState);

        return APIPost(dispatch, apiURLBuilder.getURL('brokerWarrantsAdvancedBuyConfirmation'), data)
            .then(function() {
                dispatch(confirmWarrantsBuySuccess());
                dispatch(outdateGlobalPosition());
                dispatch(outdateMyInvestments());
            }, function(response) {
                dispatch(confirmWarrantsBuyFailure(response));
                const errorKey = get(response, ["body", "error"], "");
                dispatch(errorActions.show("brokerAPIErrors-" + errorKey, true, history));
            }
        );
    };
}

function signaturePatternSuccess(token) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_SIGNATURE_SUCCESS,
        payload: {
            token
        }
    };
}

const onValidSignaturePattern = (dispatch, getState, dataToken, history) => {
    dispatch(signaturePatternSuccess(dataToken));
    dispatch(confirmWarrantsBuy(history));
};

const requestSignaturePatternValidation = (history) => (dispatch, getState) => {
    const callback = (dispatch, getState, dataToken) => {
        dispatch(onValidSignaturePattern(dispatch, getState, dataToken, history));
    };
    signaturePatternActions.validateAndExecute(dispatch, getState, callback).catch(
        (error) => dispatch(confirmWarrantsBuyFailure(error))
    );
};

const requestSignaturePatternConfiguration = (history) => (dispatch, getState) => {
    const immBrokerWarrantsAdvancedBuy = getState().brokerWarrantsAdvancedBuy;
    const immStocksContract = getState().brokerStocksContract;

    const immBrokerWarrantsAdvancedBuyStep2 = immBrokerWarrantsAdvancedBuy.getIn(['steps', SECOND_STEP]);

    const selectedOptionValue = immBrokerWarrantsAdvancedBuyStep2.get('selectedOptionValue');
    const selectedStockContract = immStocksContract.get(selectedOptionValue);

    const productNumber = selectedStockContract.get('productNumber');
    const walletNumber = selectedStockContract.get('walletNumber');

    const params = {
        chargeAccount: `${CHARGE_ACCOUNT_PREFIX}${productNumber}${walletNumber}`,
        bondsNumber: immBrokerWarrantsAdvancedBuy.getIn(['validationServiceResponse', 'ntitulos'])
    };

    signaturePatternActions.processRequestConfiguration(BROKER_WARRANTS_ADVANCED_BUY_ID, params)(dispatch).then(
        () => dispatch(requestSignaturePatternValidation(history)),
        (error) => dispatch(confirmWarrantsBuyFailure(error))
    );
};

const confirmOrder = (history) => (dispatch, getState) => {
    const immBrokerWarrantsAdvancedBuy = getState().brokerWarrantsAdvancedBuy;
    const valid = immBrokerWarrantsAdvancedBuy.getIn(['steps', THIRD_STEP, 'valid']);
    const loading = immBrokerWarrantsAdvancedBuy.getIn(['steps', THIRD_STEP, 'loading']);

    if (valid && !loading) {
        dispatch(confirmWarrantsBuyRequest());
        dispatch(requestSignaturePatternConfiguration(history));
    }
};

const validateStep = (step, history) => (dispatch, getState) => {
    const immBrokerWarrantsAdvancedBuy = getState().brokerWarrantsAdvancedBuy;

    dispatch(validateUIStep(step));

    switch (step) {
        case WIZARD_SECOND_STEP:
            if (!immBrokerWarrantsAdvancedBuy.getIn(['steps', SECOND_STEP, 'loading'])) {
                dispatch(validateWithAPI(step, history));
            }
            break;
        case WIZARD_THIRD_STEP:
            dispatch(confirmOrder(history));
            break;
    }
};

function finish() {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_FINISH
    };
}

function orderTermsChanged(value) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_ORDER_TERMS_CHANGED,
        payload: {
            value
        }
    };
}

const orderConditionValueChanged = (value) => {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_ORDER_CONDITION_VALUE_CHANGED,
        payload: {
            value
        }
    };
};

const conditionTypeChanged = (value) => {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_ORDER_CONDITION_TYPE_CHANGED,
        payload: {
            value
        }
    };
}

const conditionTypeValueChanged = (value) => {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_CONDITION_TYPE_VALUE_CHANGED,
        payload: {
            value
        }
    };
};

const orderTermValidityDateChanged = (value) => {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_ORDER_TERM_VALIDITY_DATE_CHANGED,
        payload: {
            value
        }
    };
};

function stockContractChanged(value) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_STOCK_CONTRACT_CHANGED,
        payload: {
            value
        }
    };
}

function titleQuantityChanged(value) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_TITLE_QUANTITY_CHANGED,
        payload: {
            value
        }
    };
}

function approxImportChanged(value) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_APPROX_IMPORT_CHANGED,
        payload: {
            value
        }
    };
}

function buyForChanged(value) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_FOR_CHANGED,
        payload: {
            value
        }
    };
}

function limitedValueChanged(value) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_LIMITED_VALUE_CHANGED,
        payload: {
            value
        }
    };
}

function setOrderID(isin, iso) {
    return {
        type: actionTypes.BROKER_WARRANTS_ADVANCED_BUY_SET_ORDER_ID,
        payload: {
            isin,
            iso
        }
    };
}

module.exports = {
    reset,
    setVisibleStep,
    setWillCancel,
    validateStep,
    finish,
    orderTermsChanged,
    orderConditionValueChanged,
    conditionTypeChanged,
    conditionTypeValueChanged,
    orderTermValidityDateChanged,
    stockContractChanged,
    titleQuantityChanged,
    buyForChanged,
    limitedValueChanged,
    approxImportChanged,
    setOrderID
};
